import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ContentHeader from './ContentHeader';
import api from '../services/api';
import './Layout.css';

const Layout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cmsMenuOpen, setCmsMenuOpen] = useState(false);
  const [licensesMenuOpen, setLicensesMenuOpen] = useState(false);
  const [supportTicketsMenuOpen, setSupportTicketsMenuOpen] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Initialize AdminLTE treeview on mount
  useEffect(() => {
    if (window.$ && window.$.fn.treeview) {
      window.$('[data-widget="treeview"]').Treeview('init');
    }
  }, []);

  // Update menu open state based on current path
  useEffect(() => {
    if (location.pathname.startsWith('/cms')) {
      setCmsMenuOpen(true);
    }
    if (location.pathname.startsWith('/licenses')) {
      setLicensesMenuOpen(true);
    }
    if (location.pathname.startsWith('/support-tickets')) {
      setSupportTicketsMenuOpen(true);
    }
  }, [location.pathname]);

  // Fetch pending plan selection requests count
  const fetchPendingRequestsCount = async () => {
    try {
      const count = await api.getPendingPlanRequestsCount();
      setPendingRequestsCount(count);
    } catch (error) {
      console.error('Error fetching pending requests count:', error);
    }
  };

  // Fetch count on mount and set up polling
  useEffect(() => {
    fetchPendingRequestsCount();
    
    // Poll every 30 seconds for new requests
    const interval = setInterval(() => {
      fetchPendingRequestsCount();
    }, 30000);

    // Also refresh when navigating to plan requests page
    if (location.pathname === '/licenses/plan-requests') {
      fetchPendingRequestsCount();
    }

    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`wrapper ${sidebarCollapsed ? 'sidebar-collapse' : ''}`}>
      {/* Navbar */}
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a className="nav-link" data-widget="pushmenu" href="#" role="button" onClick={(e) => { e.preventDefault(); setSidebarCollapsed(!sidebarCollapsed); }}>
              <i className="fas fa-bars"></i>
            </a>
          </li>
        </ul>

        <ul className="navbar-nav ml-auto">
          {/* Notifications */}
          <li className="nav-item dropdown">
            <a 
              className="nav-link" 
              data-toggle="dropdown" 
              href="#"
            >
              <i className="far fa-bell"></i>
              {pendingRequestsCount > 0 && (
                <span className="badge badge-warning navbar-badge">{pendingRequestsCount}</span>
              )}
            </a>
            <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
              <span className="dropdown-item dropdown-header">
                {pendingRequestsCount} Pending Plan Request{pendingRequestsCount !== 1 ? 's' : ''}
              </span>
              <div className="dropdown-divider"></div>
              <a 
                href="#" 
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/licenses/plan-requests');
                }}
              >
                <i className="fas fa-key mr-2"></i> View Plan Requests
              </a>
            </div>
          </li>
          
          {/* User Menu */}
          <li className="nav-item dropdown">
            <a className="nav-link" data-toggle="dropdown" href="#">
              <i className="far fa-user"></i>
            </a>
            <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
              <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); }}>
                <i className="fas fa-user mr-2"></i> {user?.username || 'Profile'}
              </a>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                <i className="fas fa-sign-out-alt mr-2"></i> Logout
              </a>
            </div>
          </li>
        </ul>
      </nav>

      {/* Sidebar */}
      <aside className="main-sidebar sidebar-dark-primary elevation-4">
        <Link to="/" className="brand-link">
          <span className="brand-text font-weight-light">Chesi Portal</span>
        </Link>

        <div className="sidebar">
          <nav className="mt-2">
            <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu">
              <li className="nav-item">
                <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-tachometer-alt"></i>
                  <p>Dashboard</p>
                </Link>
              </li>
              
              <li className={`nav-item has-treeview ${cmsMenuOpen ? 'menu-open' : ''}`}>
                <a 
                  href="#" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setCmsMenuOpen(!cmsMenuOpen);
                  }}
                >
                  <i className="nav-icon fas fa-edit"></i>
                  <p>
                    CMS
                    <i className={`right fas fa-angle-${cmsMenuOpen ? 'down' : 'left'}`}></i>
                  </p>
                </a>
                <ul className={`nav nav-treeview ${cmsMenuOpen ? '' : 'display-none'}`} style={{ display: cmsMenuOpen ? 'block' : 'none' }}>
                  <li className="nav-item">
                    <Link to="/cms" className={`nav-link ${location.pathname === '/cms' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Overview</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/cms/pages" className={`nav-link ${location.pathname === '/cms/pages' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Pages</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/cms/content" className={`nav-link ${location.pathname === '/cms/content' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Content</p>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className={`nav-item has-treeview ${licensesMenuOpen ? 'menu-open' : ''}`}>
                <a 
                  href="#" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    setLicensesMenuOpen(!licensesMenuOpen);
                  }}
                >
                  <i className="nav-icon fas fa-key"></i>
                  <p>
                    Licenses
                    <i className={`right fas fa-angle-${licensesMenuOpen ? 'down' : 'left'}`}></i>
                  </p>
                </a>
                <ul className={`nav nav-treeview ${licensesMenuOpen ? '' : 'display-none'}`} style={{ display: licensesMenuOpen ? 'block' : 'none' }}>
                  <li className="nav-item">
                    <Link to="/licenses" className={`nav-link ${location.pathname === '/licenses' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>All Licenses</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/licenses/tiers" className={`nav-link ${location.pathname === '/licenses/tiers' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>License Tiers</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/licenses/create" className={`nav-link ${location.pathname === '/licenses/create' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Create License</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/licenses/plan-requests" className={`nav-link ${location.pathname === '/licenses/plan-requests' ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Plan Requests</p>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className="nav-item">
                <Link to="/support-tickets" className={`nav-link ${location.pathname.startsWith('/support-tickets') ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-ticket-alt"></i>
                  <p>Support Tickets</p>
                </Link>
              </li>

              <li className="nav-item">
                <Link to="/reports" className={`nav-link ${location.pathname.startsWith('/reports') ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-chart-line"></i>
                  <p>Reports</p>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </aside>

      {/* Content Wrapper */}
      <div className="content-wrapper">
        {location.pathname !== '/' && <ContentHeader location={location} />}
        <section className="content">
          <div className={location.pathname === '/' ? '' : 'container-fluid'}>
            {children}
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="main-footer">
        <strong>Copyright &copy; 2025 <a href="#">Chesi Mobile Solutions Ltd</a>.</strong>
        All rights reserved.
      </footer>
    </div>
  );
};

export default Layout;

